import { trapFocus, removeTrapFocus } from '@theme/focus';

const SECTION_ID = 'irl-quick-add';

/** @type {Map<string, string>} */
const contentCache = new Map();

/** @type {Element | null} */
let lastFocused = null;

let activeDrawer = null;

const portaledDrawers = new WeakMap();

function drawerHost(drawer) {
  return drawer.closest('[data-irl-scope]') || drawer.closest('.shopify-section');
}

function drawerFor(trigger) {
  const own = trigger.closest('[data-irl-drawer]');
  if (own) return own;

  for (const host of [trigger.closest('[data-irl-scope]'), trigger.closest('.shopify-section')]) {
    if (!host) continue;
    const portaled = portaledDrawers.get(host);
    if (portaled?.isConnected) return portaled;
    const inPlace = host.querySelector('[data-irl-drawer]');
    if (inPlace) return inPlace;
  }

  return [...document.querySelectorAll('[data-irl-drawer]')].find((d) => !d.closest('dialog')) || null;
}

async function fetchContent(url) {
  const cached = contentCache.get(url);
  if (cached) return cached;

  const requestUrl = new URL(url, window.location.origin);
  requestUrl.searchParams.set('section_id', SECTION_ID);

  const response = await fetch(requestUrl);
  if (!response.ok) return null;

  const markup = await response.text();
  contentCache.set(url, markup);
  return markup;
}

function closeDrawer() {
  openRequestToken++;
  const element = activeDrawer;
  if (!element) return;

  element.classList.remove('is-open');
  if (!element.closest('dialog')) {
    document.body.style.removeProperty('overflow');
    removeTrapFocus();
  }
  element.closest('dialog')?.querySelector('.cart-drawer__inner')?.removeAttribute('inert');
  activeDrawer = null;

  if (lastFocused instanceof HTMLElement) lastFocused.focus();
  lastFocused = null;
}

let openRequestToken = 0;

function storedCartItemCount() {
  try {
    const raw = sessionStorage.getItem('cart-count');
    if (!raw) return null;
    const { value } = JSON.parse(raw);
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

function resolveCartItemCount(cart, addedQty) {
  if (typeof cart?.item_count === 'number') return cart.item_count;
  const stored = storedCartItemCount();
  if (stored != null) return stored + addedQty;
  return addedQty;
}

function syncDrawerTitle(drawer, trigger) {
  const scope = trigger?.closest('[data-irl-scope]');
  const fromScope = scope?.dataset.irlDrawerTitle?.trim();
  const titleEl = drawer.querySelector('.irl-drawer__title');
  const inDrawer =
    trigger instanceof Element && Boolean(trigger.closest('[data-irl-drawer]'));
  const title =
    fromScope ||
    (inDrawer ? titleEl?.textContent?.trim() : '') ||
    drawer.dataset.defaultTitle ||
    'Quick Add';
  const panel = drawer.querySelector('[data-irl-drawer-panel]');
  if (titleEl) titleEl.textContent = title;
  panel?.setAttribute('aria-label', title);
}

async function openDrawer(url, element) {
  const token = ++openRequestToken;
  if (activeDrawer && activeDrawer !== element) {
    activeDrawer.classList.remove('is-open');
    activeDrawer.querySelector('[data-irl-drawer-body]')?.replaceChildren();
  }

  const body = element.querySelector('[data-irl-drawer-body]');
  if (!body) return;

  const isContentSwap = activeDrawer === element && element.classList.contains('is-open');
  if (isContentSwap) body.setAttribute('data-irl-loading', '');

  const hostDialog = element.closest('dialog');

  let markup = null;
  try {
    markup = await fetchContent(url);
  } catch {
    markup = null;
  }
  if (!markup || token !== openRequestToken) {
    if (isContentSwap && token === openRequestToken) body.removeAttribute('data-irl-loading');
    if (token !== openRequestToken) return;
    if (!element.isConnected || (hostDialog && !hostDialog.open)) return;
    body.innerHTML =
      '<p class="irl-quick-add__error" role="alert">Could not load this product. Check your connection and try again.</p>';
  } else {
    if (!element.isConnected || (hostDialog && !hostDialog.open)) {
      if (isContentSwap) body.removeAttribute('data-irl-loading');
      return;
    }
    body.removeAttribute('data-irl-loading');
    body.innerHTML = markup;
    const content = body.querySelector('[data-irl-quick-add]');
    if (content) syncVariant(content);
  }

  const wasOpen = element.classList.contains('is-open');
  if (!hostDialog && element.parentElement !== document.body) {
    const host = drawerHost(element);
    if (host) portaledDrawers.set(host, element);
    document.body.append(element);
    element.getBoundingClientRect();
  }
  element.classList.add('is-open');
  activeDrawer = element;
  if (!hostDialog) {
    document.body.style.overflow = 'hidden';
    trapFocus(element.querySelector('[data-irl-drawer-panel]') ?? element);
  }
  if (!wasOpen) {
    hostDialog?.addEventListener('close', () => closeDrawer(), { once: true });
  }
  hostDialog?.querySelector('.cart-drawer__inner')?.setAttribute('inert', '');

  const alert = body.querySelector('[role="alert"]');
  if (alert instanceof HTMLElement) {
    alert.setAttribute('tabindex', '-1');
    alert.focus();
  } else {
    const activeSwatch = isContentSwap
      ? body.querySelector('.irl-quick-add__colours [aria-current]')
      : null;
    if (activeSwatch instanceof HTMLElement) {
      activeSwatch.focus();
    } else {
      const close = element.querySelector('[data-irl-drawer-close]');
      if (close instanceof HTMLElement) close.focus();
    }
  }
}

function syncVariant(root) {
  const script = root.querySelector('[data-irl-quick-add-variants]');
  const variantIdInput = root.querySelector('[data-irl-quick-add-variant-id]');
  const submit = root.querySelector('[data-irl-quick-add-submit]');
  if (!script || !(variantIdInput instanceof HTMLInputElement)) return;

  /** @type {Array<{id: number, options: string[], available: boolean, price: string, compareAtPrice: string | null}>} */
  let variants = [];
  try {
    variants = JSON.parse(script.textContent || '[]');
  } catch {
    return;
  }

  const selected = [...root.querySelectorAll('[data-irl-quick-add-option]')].map((fieldset) => {
    const checked = fieldset.querySelector('input:checked');
    return checked instanceof HTMLInputElement ? checked.value : null;
  });

  const match = selected.length
    ? variants.find((variant) => variant.options.every((option, index) => option === selected[index]))
    : variants[0];

  if (match) variantIdInput.value = String(match.id);

  [...root.querySelectorAll('[data-irl-quick-add-option]')].forEach((fieldset, index) => {
    fieldset.querySelectorAll('[data-irl-quick-add-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const possible = variants.some(
        (variant) =>
          variant.available &&
          variant.options[index] === input.value &&
          selected.every((value, position) => position === index || value == null || variant.options[position] === value)
      );

      input.closest('.irl-quick-add__value')?.classList.toggle('irl-quick-add__value--unavailable', !possible);
    });
  });

  const price = root.querySelector('[data-irl-quick-add-price]');
  if (price && match) {
    price.innerHTML = match.compareAtPrice
      ? `<s>${match.compareAtPrice}</s> <span class="irl-quick-add__price-sale">${match.price}</span>`
      : match.price;
  }

  if (submit instanceof HTMLButtonElement) {
    const available = Boolean(match && match.available);
    submit.disabled = !available;
    submit.textContent = available
      ? submit.dataset.labelAvailable || submit.textContent
      : submit.dataset.labelSoldOut || submit.textContent;
  }
}

async function addToCart(form) {
  const submit = form.querySelector('[data-irl-quick-add-submit]');
  const errorEl = form.querySelector('[data-irl-quick-add-error]');
  const root = form.closest('[data-irl-quick-add]');

  if (submit instanceof HTMLButtonElement) {
    if (submit.getAttribute('aria-busy') === 'true') return;
    submit.setAttribute('aria-busy', 'true');
    submit.disabled = true;
  }
  if (errorEl instanceof HTMLElement) {
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  try {
    let response;
    try {
      response = await fetch(
        window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js',
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        }
      );
    } catch {
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = 'Could not add to bag. Check your connection and try again.';
        errorEl.hidden = false;
      }
      return;
    }

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        (body && (body.description || body.message)) || 'Could not add to bag';
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      return;
    }

    const added = await response.json().catch(() => null);
    const addedQty = Number(new FormData(form).get('quantity')) || 1;

    const cartUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js';
    const cart = await fetch(cartUrl)
      .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
      .catch(() => null);

    const itemCount = resolveCartItemCount(cart, addedQty);

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          data: {
            itemCount,
            source: 'irl-quick-add',
            productId: root?.dataset.productId,
            addedItems: added ? [{ ...added, quantity: addedQty }] : [],
          },
        },
      })
    );
    closeDrawer();
  } catch {
    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = 'Could not add to bag. Please try again.';
      errorEl.hidden = false;
    }
  } finally {
    if (submit instanceof HTMLButtonElement) {
      submit.removeAttribute('aria-busy');
      if (root) syncVariant(root);
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('.irl-hotspot__trigger, [data-irl-trigger]');
  if (trigger instanceof HTMLElement && trigger.dataset.productUrl) {
    const element = drawerFor(trigger);
    if (!element) return;

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    const insideOpenDrawer = activeDrawer === element && element.classList.contains('is-open') && element.contains(trigger);
    if (!insideOpenDrawer) lastFocused = trigger;
    syncDrawerTitle(element, trigger);
    openDrawer(trigger.dataset.productUrl, element);
    return;
  }

  if (target.closest('[data-irl-drawer-close]')) closeDrawer();
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches('[data-irl-quick-add-input]')) return;

  const root = target.closest('[data-irl-quick-add]');
  if (root) syncVariant(root);
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.closest('[data-irl-quick-add]')) return;

  event.preventDefault();
  addToCart(form);
});

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Escape') return;
    if (!activeDrawer?.classList.contains('is-open')) return;
    if (activeDrawer.checkVisibility && !activeDrawer.checkVisibility()) {
      closeDrawer();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    closeDrawer();
  },
  { capture: true }
);
